import { PageContainer } from '@ant-design/pro-layout'
import { makeStyles } from '@material-ui/styles'
import { Col, Collapse, Modal, Row, Table, Typography as Text } from 'antd'
import React, { useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'

import Card from '@/components/Card'
import DurationFilterButton from '@/components/DurationFilterButton'
import FilterDialog from '@/components/FilterDialog'
import LoadingIndicator from '@/components/LoadingIndicator'
import ReportActionButtons from '@/components/ReportActionButtons'
import ReportPrintContainer from '@/components/ReportPrintContainer'
import ReportResultCounts from '@/components/ReportResultCounts'
import { Bar } from '@/components/chart'
import { pageStyle } from '@/constants'
import dimorderApi from '@/libs/api/dimorderApi'
import { currencyWithCommas, numberWithCommas } from '@/libs/numberWithCommas'
import { useSelector } from '@/redux'
import colors from '@/style/colors'

export default function HourlySalesReport(props) {
  const printRef = useRef()
  const classes = useStyles(props)
  const { Panel } = Collapse
  const [params, setParams] = React.useState(null)
  const [salesReport, setSalesReport] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [activeKey, setActiveKey] = React.useState('1')
  const [numberOfResults, setNumberOfResults] = React.useState(0)
  const [shouldShowTax, setShouldShowTax] = React.useState(false)
  const { t } = useTranslation()
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const currentLang = useSelector((state) => state.app.lang)

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    stacked: false,
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: t('app.constant.order.totalSales'),
          color: colors.data1,
          font: {
            size: 14,
          },
        },
        ticks: {
          color: colors.data1,
          font: {
            size: 14,
          },
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        // grid line settings
        grid: {
          drawOnChartArea: false, // only want the grid lines for one axis to show up
        },
        title: {
          display: true,
          text: t('page.hourlySalesReport.transaction'),
          color: colors.data5,
          font: {
            size: 14,
          },
        },
        ticks: {
          color: colors.data5,
          font: {
            size: 14,
          },
        },
      },
    },
  }

  React.useEffect(() => {
    if (params) {
      const getItems = async () => {
        setLoading(true)
        try {
          const salesReport = await dimorderApi
            .instance(activeMerchant.id)
            ?.getHourlySalesReport(params)
          setSalesReport(salesReport)
          setShouldShowTax(
            activeMerchant?.setting?.enableTax ||
              salesReport?.main?.total?.taxTotal > 0 ||
              salesReport?.compare?.total?.taxTotal > 0,
          )
          setActiveKey('0')
        } catch (error) {
          console.log(error)
          Modal.error({
            title: t('app.common.error'),
            content: t('app.common.submitError'),
          })
          setLoading(false)
          setActiveKey('1')
        }
        setLoading(false)
      }
      getItems()
    }
  }, [params, t, activeMerchant, currentLang])

  React.useEffect(() => {
    setNumberOfResults(salesReport?.main?.byHour.length)
  }, [salesReport])

  const salesReportChart = useMemo(() => {
    const chartData = {
      labels: [],
      datasets: [
        {
          type: 'line',
          label: t('page.hourlySalesReport.report.transaction'),
          borderColor: colors.data5,
          backgroundColor: colors.white,
          borderWidth: 3,
          fill: false,
          data: [],
          yAxisID: 'y1',
          order: 2,
        },
        {
          type: 'bar',
          label: t('page.hourlySalesReport.report.totalSales'),
          backgroundColor: colors.data1,
          data: [],
          yAxisID: 'y',
          order: 3,
        },
      ],
    }

    if (salesReport?.main) {
      for (const [, value] of Object.entries(salesReport.main.byHour)) {
        chartData.labels.push(`${value.hourStart} - ${value.hourEnd}`)
        chartData.datasets[0].data.push(value.numTransactions)
        chartData.datasets[1].data.push(value.totalSales)
      }
    }

    if (params?.compare) {
      chartData.datasets.push(
        {
          type: 'line',
          label: t('page.hourlySalesReport.compare.transaction'),
          borderColor: colors.data8,
          backgroundColor: colors.white,
          borderWidth: 3,
          fill: false,
          data: [],
          yAxisID: 'y1',
          order: 1,
        },
        {
          type: 'bar',
          label: t('page.hourlySalesReport.compare.totalSales'),
          backgroundColor: colors.data3,
          data: [],
          yAxisID: 'y',
          order: 4,
        },
      )
      if (salesReport?.compare) {
        for (const [, value] of Object.entries(salesReport.compare.byHour)) {
          chartData.datasets[2].data.push(value.numTransactions)
          chartData.datasets[3].data.push(value.totalSales)
        }
      }
    }

    return chartData
  }, [salesReport?.main?.byHour, salesReport?.compare?.byHour, params?.compare, t])

  const reportColumns = [
    {
      title: t('app.common.time'),
      dataIndex: 'time',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => parseInt(a.time.slice(0, 5)) - parseInt(b.time.slice(0, 5)),
    },
    {
      title: t('app.constant.order.totalSales'),
      dataIndex: 'amount',
      sorter: (a, b) => a.amount - b.amount,
      render: (amount) => (
        <Text>
          {currencyWithCommas(
            amount,
            activeMerchant?.setting?.currency,
            activeMerchant?.rounding?.minFractionDigits,
          )}
        </Text>
      ),
    },
    {
      title: t('app.constant.order.discount'),
      dataIndex: 'discount',
      sorter: (a, b) => a.discount - b.discount,
      render: (discount) => (
        <Text>
          {currencyWithCommas(
            discount,
            activeMerchant?.setting?.currency,
            activeMerchant?.rounding?.minFractionDigits,
          )}
        </Text>
      ),
    },
    {
      title: t('page.discountReport.promocode'),
      dataIndex: 'promocode',
      sorter: (a, b) => a.promocode - b.promocode,
      render: (promocode) => (
        <Text>
          {currencyWithCommas(
            promocode,
            activeMerchant?.setting?.currency,
            activeMerchant?.rounding?.minFractionDigits,
          )}
        </Text>
      ),
    },
    {
      title: t('app.constant.order.surcharge'),
      dataIndex: 'surcharge',
      sorter: (a, b) => a.surcharge - b.surcharge,
      render: (surcharge) => (
        <Text>
          {currencyWithCommas(
            surcharge,
            activeMerchant?.setting?.currency,
            activeMerchant?.rounding?.minFractionDigits,
          )}
        </Text>
      ),
    },
  ]

  if (shouldShowTax) {
    reportColumns.push(
      {
        title: t('app.constant.order.totalBeforeTax'),
        dataIndex: 'totalBeforeTax',
        sorter: (a, b) => a.totalBeforeTax - b.totalBeforeTax,
        render: (totalBeforeTax) => (
          <Text>
            {currencyWithCommas(
              totalBeforeTax,
              activeMerchant?.setting?.currency,
              activeMerchant?.rounding?.minFractionDigits,
            )}
          </Text>
        ),
      },
      {
        title: t('app.constant.order.taxTotal'),
        dataIndex: 'taxTotal',
        sorter: (a, b) => a.taxTotal - b.taxTotal,
        render: (taxTotal) => (
          <Text>
            {currencyWithCommas(
              taxTotal,
              activeMerchant?.setting?.currency,
              activeMerchant?.rounding?.minFractionDigits,
            )}
          </Text>
        ),
      },
    )
  }

  reportColumns.push(
    {
      title: t('page.hourlySalesReport.transaction'),
      dataIndex: 'transaction',
      sorter: (a, b) => a.transaction - b.transaction,
    },
    {
      title: t('page.hourlySalesReport.customers'),
      dataIndex: 'customers',
      sorter: (a, b) => a.customers - b.customers,
    },
  )

  const reportData = {
    columns: reportColumns,
    data: [],
  }

  const compareData = {
    columns: reportColumns,
    data: [],
  }

  if (salesReport?.main) {
    for (const [key, value] of Object.entries(salesReport.main.byHour)) {
      reportData.data.push({
        key,
        time: `${value.hourStart} - ${value.hourEnd}`,
        amount: value.totalSales,
        discount: value.totalDiscount,
        promocode: value.totalPromocode,
        surcharge: value.totalSurcharge,
        totalBeforeTax: value.totalBeforeTax,
        taxTotal: value.taxTotal,
        transaction: value.numTransactions,
        customers: value.numCustomers,
      })
    }
  }

  if (params?.compare) {
    if (salesReport?.compare) {
      for (const [key, value] of Object.entries(salesReport.compare.byHour)) {
        compareData.data.push({
          key,
          time: `${value.hourStart} - ${value.hourEnd}`,
          amount: value.totalSales,
          discount: value.totalDiscount,
          promocode: value.totalPromocode,
          surcharge: value.totalSurcharge,
          totalBeforeTax: value.totalBeforeTax,
          taxTotal: value.taxTotal,
          transaction: value.numTransactions,
          customers: value.numCustomers,
        })
      }
    }
  }

  const displayResult = () => (
    <>
      <Row gutter={16}>
        <Col span={24} className={classes.container}>
          <Card
            width='100%'
            height='100%'
            padding='20px'
            borderRadius='2px'
            textAlign='center'
            boxShadow='1px 1px  5px rgba(0, 0, 0, 0.3)'
          >
            <Text className={classes.title}>{t('page.hourlySalesReport.chart')}</Text>
            <Bar data={salesReportChart} options={options} height={350} width='98%' />
          </Card>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xl={params.compare ? 12 : 24} xs={24} className={classes.container}>
          <Card
            width='100%'
            height='100%'
            padding='20px'
            borderRadius='2px'
            textAlign='left'
            boxShadow='1px 1px  5px rgba(0, 0, 0, 0.3)'
          >
            <Text className={classes.title}>{t('page.hourlySalesReport.report.table')}</Text>
            <br />
            <ReportResultCounts counts={numberOfResults} />
            <br />
            <Table
              columns={reportData.columns}
              dataSource={reportData.data}
              scroll={{ x: 500 }}
              pagination={false}
              summary={() => {
                const totalSales = salesReport?.main?.total?.sales
                const totalCustomerCount = salesReport?.main?.total?.numCustomers
                const totalTransactionCount = salesReport?.main?.total?.numTransactions
                const totalDiscount = salesReport?.main?.total?.discount
                const totalPromocode = salesReport?.main?.total?.promocode
                const totalSurcharge = salesReport?.main?.total?.surcharge
                const totalBeforeTax = salesReport?.main?.total?.totalBeforeTax
                const taxTotal = salesReport?.main?.total?.taxTotal
                return (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {t('page.salesReportByItem.total')}
                        </Text>
                      </Table.Summary.Cell>

                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {currencyWithCommas(
                            totalSales,
                            activeMerchant?.setting?.currency,
                            activeMerchant?.rounding?.minFractionDigits,
                          )}
                        </Text>
                      </Table.Summary.Cell>

                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {currencyWithCommas(
                            totalDiscount,
                            activeMerchant?.setting?.currency,
                            activeMerchant?.rounding?.minFractionDigits,
                          )}
                        </Text>
                      </Table.Summary.Cell>

                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {currencyWithCommas(
                            totalPromocode,
                            activeMerchant?.setting?.currency,
                            activeMerchant?.rounding?.minFractionDigits,
                          )}
                        </Text>
                      </Table.Summary.Cell>

                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {currencyWithCommas(
                            totalSurcharge,
                            activeMerchant?.setting?.currency,
                            activeMerchant?.rounding?.minFractionDigits,
                          )}
                        </Text>
                      </Table.Summary.Cell>
                      {shouldShowTax && (
                        <>
                          <Table.Summary.Cell>
                            <Text className={classes.totalText}>
                              {currencyWithCommas(
                                totalBeforeTax,
                                activeMerchant?.setting?.currency,
                                activeMerchant?.rounding?.minFractionDigits,
                              )}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text className={classes.totalText}>
                              {currencyWithCommas(
                                taxTotal,
                                activeMerchant?.setting?.currency,
                                activeMerchant?.rounding?.minFractionDigits,
                              )}
                            </Text>
                          </Table.Summary.Cell>
                        </>
                      )}
                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {numberWithCommas(totalTransactionCount)}
                        </Text>
                      </Table.Summary.Cell>

                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {numberWithCommas(totalCustomerCount)}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                )
              }}
            />
          </Card>
        </Col>
        {params.compare && (
          <Col xl={12} xs={24} className={classes.container}>
            <Card
              width='100%'
              height='100%'
              padding='20px'
              borderRadius='2px'
              textAlign='left'
              boxShadow='1px 1px  5px rgba(0, 0, 0, 0.3)'
            >
              <Text className={classes.title}>{t('page.hourlySalesReport.compare.table')}</Text>
              <br />
              <ReportResultCounts counts={numberOfResults} />
              <br />
              <Table
                columns={compareData.columns}
                dataSource={compareData.data}
                scroll={{ x: 500 }}
                pagination={false}
                summary={() => {
                  const totalSales = salesReport?.compare?.total?.sales
                  const totalCustomerCount = salesReport?.compare?.total?.numCustomers
                  const totalTransactionCount = salesReport?.compare?.total?.numTransactions
                  const totalDiscount = salesReport?.compare?.total?.discount
                  const totalPromocode = salesReport?.compare?.total?.promocode
                  const totalSurcharge = salesReport?.compare?.total?.surcharge
                  const totalBeforeTax = salesReport?.compare?.total?.totalBeforeTax
                  const taxTotal = salesReport?.compare?.total?.taxTotal
                  return (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>
                            {t('page.salesReportByItem.total')}
                          </Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>
                            {currencyWithCommas(
                              totalSales,
                              activeMerchant?.setting?.currency,
                              activeMerchant?.rounding?.minFractionDigits,
                            )}
                          </Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>
                            {currencyWithCommas(
                              totalDiscount,
                              activeMerchant?.setting?.currency,
                              activeMerchant?.rounding?.minFractionDigits,
                            )}
                          </Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>
                            {currencyWithCommas(
                              totalPromocode,
                              activeMerchant?.setting?.currency,
                              activeMerchant?.rounding?.minFractionDigits,
                            )}
                          </Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>
                            {currencyWithCommas(
                              totalSurcharge,
                              activeMerchant?.setting?.currency,
                              activeMerchant?.rounding?.minFractionDigits,
                            )}
                          </Text>
                        </Table.Summary.Cell>

                        {shouldShowTax && (
                          <>
                            <Table.Summary.Cell>
                              <Text className={classes.totalText}>
                                {currencyWithCommas(
                                  totalBeforeTax,
                                  activeMerchant?.setting?.currency,
                                  activeMerchant?.rounding?.minFractionDigits,
                                )}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell>
                              <Text className={classes.totalText}>
                                {currencyWithCommas(
                                  taxTotal,
                                  activeMerchant?.setting?.currency,
                                  activeMerchant?.rounding?.minFractionDigits,
                                )}
                              </Text>
                            </Table.Summary.Cell>
                          </>
                        )}

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>{totalTransactionCount}</Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>{totalCustomerCount}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>
                  )
                }}
              />
            </Card>
          </Col>
        )}
      </Row>
    </>
  )

  const printResult = () => (
    <ReportPrintContainer
      printRef={printRef}
      start={params?.start}
      end={params?.end}
      title={t('page.hourlySalesReport.title')}
    >
      <Row gutter={16}>
        <Col span={24} className={classes.printContainer}>
          <>
            <Text className={classes.title}>{t('page.hourlySalesReport.chart')}</Text>
            <Bar data={salesReportChart} options={options} height={350} width={1200} />
          </>
        </Col>
      </Row>
      <Row gutter={60}>
        <Col span={params.compare ? 12 : 24} className={classes.printContainer}>
          <>
            <Text className={classes.title}>{t('page.hourlySalesReport.report.table')}</Text>
            <br />
            <ReportResultCounts counts={numberOfResults} />
            <br />
            <Table
              columns={reportData.columns}
              dataSource={reportData.data}
              scroll={{ x: 500 }}
              pagination={false}
              summary={() => {
                const totalSales = salesReport?.main?.total?.sales
                const totalCustomerCount = salesReport?.main?.total?.numCustomers
                const totalTransactionCount = salesReport?.main?.total?.numTransactions
                const totalDiscount = salesReport?.main?.total?.discount
                const totalPromocode = salesReport?.main?.total?.promocode
                const totalSurcharge = salesReport?.main?.total?.surcharge
                const totalBeforeTax = salesReport?.main?.total?.totalBeforeTax
                const taxTotal = salesReport?.main?.total?.taxTotal
                return (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {t('page.salesReportByItem.total')}
                        </Text>
                      </Table.Summary.Cell>

                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {currencyWithCommas(
                            totalSales,
                            activeMerchant?.setting?.currency,
                            activeMerchant?.rounding?.minFractionDigits,
                          )}
                        </Text>
                      </Table.Summary.Cell>

                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {currencyWithCommas(
                            totalDiscount,
                            activeMerchant?.setting?.currency,
                            activeMerchant?.rounding?.minFractionDigits,
                          )}
                        </Text>
                      </Table.Summary.Cell>

                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {currencyWithCommas(
                            totalPromocode,
                            activeMerchant?.setting?.currency,
                            activeMerchant?.rounding?.minFractionDigits,
                          )}
                        </Text>
                      </Table.Summary.Cell>

                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>
                          {currencyWithCommas(
                            totalSurcharge,
                            activeMerchant?.setting?.currency,
                            activeMerchant?.rounding?.minFractionDigits,
                          )}
                        </Text>
                      </Table.Summary.Cell>
                      {shouldShowTax && (
                        <>
                          <Table.Summary.Cell>
                            <Text className={classes.totalText}>
                              {currencyWithCommas(
                                totalBeforeTax,
                                activeMerchant?.setting?.currency,
                                activeMerchant?.rounding?.minFractionDigits,
                              )}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text className={classes.totalText}>
                              {currencyWithCommas(
                                taxTotal,
                                activeMerchant?.setting?.currency,
                                activeMerchant?.rounding?.minFractionDigits,
                              )}
                            </Text>
                          </Table.Summary.Cell>
                        </>
                      )}
                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>{totalTransactionCount}</Text>
                      </Table.Summary.Cell>

                      <Table.Summary.Cell>
                        <Text className={classes.totalText}>{totalCustomerCount}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                )
              }}
            />
          </>
        </Col>
        {params.compare && (
          <Col span={12} className={classes.printContainer}>
            <>
              <Text className={classes.title}>{t('page.hourlySalesReport.compare.table')}</Text>
              <br />
              <ReportResultCounts counts={numberOfResults} />
              <br />
              <Table
                columns={compareData.columns}
                dataSource={compareData.data}
                scroll={{ x: 500 }}
                pagination={false}
                summary={() => {
                  const totalSales = salesReport?.compare?.total?.sales
                  const totalCustomerCount = salesReport?.compare?.total?.numCustomers
                  const totalTransactionCount = salesReport?.compare?.total?.numTransactions
                  const totalDiscount = salesReport?.compare?.total?.discount
                  const totalPromocode = salesReport?.compare?.total?.promocode
                  const totalSurcharge = salesReport?.compare?.total?.surcharge
                  const totalBeforeTax = salesReport?.compare?.total?.totalBeforeTax
                  const taxTotal = salesReport?.compare?.total?.taxTotal
                  return (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>
                            {t('page.salesReportByItem.total')}
                          </Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>
                            {currencyWithCommas(
                              totalSales,
                              activeMerchant?.setting?.currency,
                              activeMerchant?.rounding?.minFractionDigits,
                            )}
                          </Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>
                            {currencyWithCommas(
                              totalDiscount,
                              activeMerchant?.setting?.currency,
                              activeMerchant?.rounding?.minFractionDigits,
                            )}
                          </Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>
                            {currencyWithCommas(
                              totalPromocode,
                              activeMerchant?.setting?.currency,
                              activeMerchant?.rounding?.minFractionDigits,
                            )}
                          </Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>
                            {currencyWithCommas(
                              totalSurcharge,
                              activeMerchant?.setting?.currency,
                              activeMerchant?.rounding?.minFractionDigits,
                            )}
                          </Text>
                        </Table.Summary.Cell>

                        {shouldShowTax && (
                          <>
                            <Table.Summary.Cell>
                              <Text className={classes.totalText}>
                                {currencyWithCommas(
                                  totalBeforeTax,
                                  activeMerchant?.setting?.currency,
                                  activeMerchant?.rounding?.minFractionDigits,
                                )}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell>
                              <Text className={classes.totalText}>
                                {currencyWithCommas(
                                  taxTotal,
                                  activeMerchant?.setting?.currency,
                                  activeMerchant?.rounding?.minFractionDigits,
                                )}
                              </Text>
                            </Table.Summary.Cell>
                          </>
                        )}

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>{totalTransactionCount}</Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell>
                          <Text className={classes.totalText}>{totalCustomerCount}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>
                  )
                }}
              />
            </>
          </Col>
        )}
      </Row>
    </ReportPrintContainer>
  )

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle,
  })

  const handleExport = async () => {
    try {
      await dimorderApi.instance(activeMerchant.id)?.exportSalesReportByHourCsv(params)
    } catch (error) {
      console.log(error)
      Modal.error({
        title: t('app.common.error'),
        content: t('app.common.exportError'),
      })
    }
  }

  return (
    <PageContainer ghost header={{ title: t('page.hourlySalesReport.title') }}>
      <Collapse
        ghost
        expandIcon={() => <></>}
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
      >
        <Panel
          key='1'
          style={{ fontSize: 22 }}
          extra={
            <DurationFilterButton start={params?.start} end={params?.end} activeKey={activeKey} />
          }
        >
          <FilterDialog compare skipCompareTime filter filterOrderType setParams={setParams} />
        </Panel>
      </Collapse>
      <br />
      <LoadingIndicator loading={loading} />
      {salesReport && !loading && (
        <>
          <ReportActionButtons onPrint={handlePrint} onExport={handleExport} />
          {displayResult()}
          {printResult()}
        </>
      )}
    </PageContainer>
  )
}

const useStyles = makeStyles(() => ({
  printContainer: {
    padding: 30,
  },
  container: {
    paddingTop: 20,
  },
  bigTitle: {
    fontSize: 24,
    textAlign: 'center',
  },
  title: {
    fontSize: 18,
    paddingBottom: 10,
  },
  totalText: {
    fontWeight: 'bold',
    fontSize: 15,
  },
}))
